/**
 * Coach qualifications schema + server functions.
 *
 * Aliases: coach qualifications, certifications, ccos, ctps, frameworks
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import * as z from "zod";

import type { GroupedCCOsByType } from "~/lib/ccos";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import {
  reviewRequestInputSchema,
  type CoachClaim,
} from "~/lib/shared/cco-claims";
import type { ClaimSuggestion } from "~/lib/shared/graduate-list";
import type { GroupedCTPsByCCO } from "~/lib/shared/types/ctps";

export const QualificationsSchema = z.object({
  id: z.string(),
  certifications: z.optional(
    z.array(
      z.object({
        id: z.optional(z.string()),
        _delete: z.boolean(),
        order: z.optional(z.number()),
        title: z.string(),
        issuer: z.string(),
        imageSrc: z.nullable(z.string()),
        url: z.nullable(z.string()),
        tempId: z.optional(z.string()),
      }),
    ),
  ),
  ccoIds: z.array(z.string()),
  frameworkIds: z.array(z.string()),
  challengeIds: z.array(z.string()),
  ctps: z.array(
    z.object({
      ctpId: z.string(),
      certificationDate: z.date().nullable(),
    }),
  ),
});

export const getCoachQualifications = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const coach = context.coach;

    const expandedCoach = await db.query.coaches.findFirst({
      where: {
        id: coach.id,
      },
      with: {
        certifications: {
          orderBy: (certs, { asc }) => asc(certs.order),
        },
        // Claims the coach removed stay hidden from their own list too.
        ccos: { where: { removedByCoachAt: { isNull: true } } },
        ctps: { where: { removedByCoachAt: { isNull: true } } },
        frameworks: true,
        challenges: true,
      },
    });

    if (!expandedCoach) {
      throw new Error("Could not find additional required coach info");
    }

    const ccoIds = expandedCoach.ccos.map((cc) => cc.ccoId);
    const frameworkIds = expandedCoach.frameworks.map((f) => f.frameworkId);
    const challengeIds = expandedCoach.challenges.map((c) => c.challengeId);
    const ctps = expandedCoach.ctps.map((ct) => ({
      ctpId: ct.ctpId,
      certificationDate: ct.certificationDate,
    }));

    return {
      ...expandedCoach,
      certifications: expandedCoach.certifications.map((c) => ({
        id: c.id,
        title: c.title,
        issuer: c.issuer,
        imageSrc: c.imageSrc,
        url: c.url,
        order: c.order,
        _delete: false,
        tempId: `existing-${c.id}`,
      })),
      ccoIds,
      frameworkIds,
      challengeIds,
      ctps,
    };
  });

/**
 * Organizations and programs a coach can pick on the qualifications page.
 * Drafts are included, so coaches may record one that is still being written
 * up; the public reads leave them out.
 */
export const getQualificationPickerOptions = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(
    async (): Promise<{ ccos: GroupedCCOsByType; ctps: GroupedCTPsByCCO }> => {
      const { getGroupedCcos } = await import("~/lib/server/cco-directory");
      const { getGroupedCtpsForPicker } = await import("~/lib/server/ctps");
      const [ccos, ctps] = await Promise.all([
        getGroupedCcos({ includeDrafts: true }),
        getGroupedCtpsForPicker(),
      ]);
      return { ccos, ctps };
    },
  );

export const saveQualifications = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(QualificationsSchema)
  .handler(async ({ data, context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { createCertification, deleteCertification, updateCertification } =
      await import("~/lib/server/coach");
    const db = await getDBWithInit();
    const coach = context.coach;

    // Process certifications in two phases: first deletions, then updates/creates
    const certificationsToProcess = data.certifications ?? [];

    // Phase 1: Handle deletions first
    const toDelete = certificationsToProcess.filter((c) => c._delete && c.id);
    for (const cert of toDelete) {
      await deleteCertification(coach.id, cert.id!);
    }

    // Phase 2: Handle updates and creates for non-deleted items
    const toKeep = certificationsToProcess.filter((c) => !c._delete);
    for (const [newOrder, c] of toKeep.entries()) {
      if (!c.id) {
        // New certification
        const { id: _id, _delete, tempId: _tempId, ...certificationData } = c;
        await createCertification({
          ...certificationData,
          coachId: coach.id,
          verified: false,
          order: newOrder,
        });
      } else {
        // Existing certification - update with new order
        const { id: _id, _delete, tempId: _tempId, ...certificationData } = c;
        await updateCertification(coach.id, c.id, {
          ...certificationData,
          order: newOrder,
        });
      }
    }

    // Memberships and programs follow the claim rules: a not-confirmed claim
    // the coach removes is kept hidden, so re-adding it keeps its decision.
    const { syncCoachClaims } = await import("~/lib/server/cco-claims");
    await syncCoachClaims({
      coachId: coach.id,
      ccoIds: data.ccoIds,
      ctps: data.ctps,
    });

    const { and, eq, inArray } = await import("drizzle-orm");
    const { coachesFrameworks, coachesChallenges } =
      await import("~/../drizzle/schema");

    // Sync Framework associations
    const currentFrameworks = await db.query.coachesFrameworks.findMany({
      where: { coachId: coach.id },
    });

    const currentFrameworkIds = new Set(
      currentFrameworks.map((f) => f.frameworkId),
    );
    const newFrameworkIds = new Set(data.frameworkIds || []);

    const fwIdsToDelete = [...currentFrameworkIds].filter(
      (id) => !newFrameworkIds.has(id),
    );
    if (fwIdsToDelete.length > 0) {
      await db
        .delete(coachesFrameworks)
        .where(
          and(
            eq(coachesFrameworks.coachId, coach.id),
            inArray(coachesFrameworks.frameworkId, fwIdsToDelete),
          ),
        );
    }

    const fwIdsToInsert = [...newFrameworkIds].filter(
      (id) => !currentFrameworkIds.has(id),
    );
    if (fwIdsToInsert.length > 0) {
      await db.insert(coachesFrameworks).values(
        fwIdsToInsert.map((frameworkId) => ({
          coachId: coach.id,
          frameworkId,
        })),
      );
    }

    // Sync Challenge associations (direct coach <-> challenge tagging)
    const currentChallenges = await db.query.coachesChallenges.findMany({
      where: { coachId: coach.id },
    });

    const currentChallengeIds = new Set(
      currentChallenges.map((c) => c.challengeId),
    );
    const newChallengeIds = new Set(data.challengeIds || []);

    const chIdsToDelete = [...currentChallengeIds].filter(
      (id) => !newChallengeIds.has(id),
    );
    if (chIdsToDelete.length > 0) {
      await db
        .delete(coachesChallenges)
        .where(
          and(
            eq(coachesChallenges.coachId, coach.id),
            inArray(coachesChallenges.challengeId, chIdsToDelete),
          ),
        );
    }

    const chIdsToInsert = [...newChallengeIds].filter(
      (id) => !currentChallengeIds.has(id),
    );
    if (chIdsToInsert.length > 0) {
      await db.insert(coachesChallenges).values(
        chIdsToInsert.map((challengeId) => ({
          coachId: coach.id,
          challengeId,
        })),
      );
    }

    // Fetch fresh data with new IDs for form reset
    const updatedCoach = await db.query.coaches.findFirst({
      where: {
        id: coach.id,
      },
      with: {
        certifications: {
          orderBy: (certs, { asc }) => asc(certs.order),
        },
        // Claims the coach removed stay hidden from their own list too.
        ccos: { where: { removedByCoachAt: { isNull: true } } },
        ctps: { where: { removedByCoachAt: { isNull: true } } },
        frameworks: true,
        challenges: true,
      },
    });

    if (!updatedCoach) {
      throw new Error("Could not find coach after save");
    }

    return {
      id: updatedCoach.id,
      certifications: updatedCoach.certifications.map((c) => ({
        id: c.id,
        title: c.title,
        issuer: c.issuer,
        imageSrc: c.imageSrc,
        url: c.url,
        order: c.order,
        _delete: false,
        tempId: `existing-${c.id}`,
      })),
      ccoIds: updatedCoach.ccos.map((cc) => cc.ccoId),
      frameworkIds: updatedCoach.frameworks.map((f) => f.frameworkId),
      challengeIds: updatedCoach.challenges.map((c) => c.challengeId),
      ctps: updatedCoach.ctps.map((ct) => ({
        ctpId: ct.ctpId,
        certificationDate: ct.certificationDate,
      })),
    };
  });

/** Each membership and program claim with its verification status. */
export const getMyClaimStatusesFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<CoachClaim[]> => {
    const { listCoachClaims } = await import("~/lib/server/cco-claims");
    return listCoachClaims(context.coach.id);
  });

export const myClaimStatusesQueryOptions = () =>
  queryOptions({
    queryKey: ["coach", "claim-statuses"],
    queryFn: () => getMyClaimStatusesFn(),
  });

/** "Ask DCM to review" on a claim that was not confirmed. */
export const requestClaimReviewFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(reviewRequestInputSchema)
  .handler(async ({ data, context }): Promise<void> => {
    const { requestReview } = await import("~/lib/server/cco-claims");
    await requestReview({ coachId: context.coach.id, ...data });
  });

/** Credentials organizations suggested from their graduate lists. */
export const getMyClaimSuggestionsFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<ClaimSuggestion[]> => {
    const { listClaimSuggestions } = await import("~/lib/server/graduate-list");
    return listClaimSuggestions(context.coach.id);
  });

export const myClaimSuggestionsQueryOptions = () =>
  queryOptions({
    queryKey: ["coach", "claim-suggestions"],
    queryFn: () => getMyClaimSuggestionsFn(),
  });

const suggestionInputSchema = z.object({ suggestionId: z.string().uuid() });

/** "Add to my profile" on a suggestion. */
export const acceptClaimSuggestionFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(suggestionInputSchema)
  .handler(async ({ data, context }): Promise<void> => {
    const { acceptClaimSuggestion } =
      await import("~/lib/server/graduate-list");
    await acceptClaimSuggestion({
      coachId: context.coach.id,
      suggestionId: data.suggestionId,
    });
  });

/** "Not now" on a suggestion. */
export const dismissClaimSuggestionFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(suggestionInputSchema)
  .handler(async ({ data, context }): Promise<void> => {
    const { dismissClaimSuggestion } =
      await import("~/lib/server/graduate-list");
    await dismissClaimSuggestion({
      coachId: context.coach.id,
      suggestionId: data.suggestionId,
    });
  });
